import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, imgUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { User, Briefcase, FileText, CheckCircle2, Upload, Check, Loader2, Trash2 } from "lucide-react";

const STEPS = ["Personal Information", "Employment & Income", "Documents", "Review & Submit"];
const DOC_TYPES = [
  { v: "drivers_license", l: "Driver's License" },
  { v: "passport", l: "Passport" },
  { v: "proof_of_address", l: "Proof of Address" },
  { v: "other", l: "Other Document" },
];

export default function Apply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("drivers_license");
  const [personal, setPersonal] = useState({});
  const [employment, setEmployment] = useState({});

  const load = () => api.get(`/applications/${id}`).then((r) => {
    setApp(r.data);
    setPersonal(r.data.personal || {});
    setEmployment(r.data.employment || {});
  }).catch(() => navigate("/dashboard"));

  useEffect(() => { window.scrollTo(0, 0); load(); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/applications/${id}`, { vehicle_id: app.vehicle_id, personal, employment, financing: app.financing });
      setApp(r.data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      return false;
    } finally { setSaving(false); }
  };

  const nextStep = async () => {
    if (step === 0) {
      const req = ["first_name", "last_name", "email", "phone", "date_of_birth", "address", "city", "state", "zip_code"];
      if (req.some((f) => !personal[f])) { toast.error("Please complete all required fields"); return; }
    }
    if (step <= 1) { const ok = await save(); if (!ok) return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await api.post(`/applications/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    const ok = await save();
    if (!ok) return;
    try {
      await api.post(`/applications/${id}/submit`);
      toast.success("Application submitted!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
  };

  if (!app) return <Layout><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div></Layout>;

  const vn = `${app.vehicle_summary.year} ${app.vehicle_summary.make} ${app.vehicle_summary.model}`;
  const field = (label, key, obj, setObj, props = {}) => (
    <div>
      <label className="mb-1.5 block text-sm text-slate-700">{label}</label>
      <Input data-testid={`apply-${key}`} value={obj[key] || ""} onChange={(e) => setObj({ ...obj, [key]: e.target.value })} className="border-slate-200 bg-[#EEF3FC] text-slate-900" {...props} />
    </div>
  );

  return (
    <Layout vehicleName={vn}>
      <div className="container-x py-10">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Financing Application</h1>
          <p className="mt-1 text-slate-600">{vn} · {fmtMoney(app.vehicle_summary.price)}</p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* Stepper */}
            <div className="mb-8">
              <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5 bg-slate-200" />
              <div className="mt-4 flex justify-between">
                {STEPS.map((s, i) => (
                  <div key={s} className={`flex flex-col items-center gap-1.5 text-center ${i <= step ? "text-gold" : "text-slate-600"}`}>
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-bold ${i < step ? "border-gold bg-gold text-[#EEF3FC]" : i === step ? "border-gold text-gold" : "border-slate-200"}`}>
                      {i < step ? <Check className="h-4 w-4" /> : i + 1}
                    </div>
                    <span className="hidden text-[11px] font-medium sm:block">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-surface p-6 sm:p-8">
              {step === 0 && (
                <div>
                  <h2 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><User className="h-5 w-5 text-gold" /> Personal Information</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {field("First Name *", "first_name", personal, setPersonal)}
                    {field("Last Name *", "last_name", personal, setPersonal)}
                    {field("Date of Birth *", "date_of_birth", personal, setPersonal, { type: "date" })}
                    {field("Email *", "email", personal, setPersonal, { type: "email" })}
                    {field("Phone *", "phone", personal, setPersonal)}
                    {field("ZIP Code *", "zip_code", personal, setPersonal)}
                    <div className="sm:col-span-2">{field("Street Address *", "address", personal, setPersonal)}</div>
                    {field("City *", "city", personal, setPersonal)}
                    {field("State *", "state", personal, setPersonal)}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h2 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><Briefcase className="h-5 w-5 text-gold" /> Employment & Income</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm text-slate-700">Employment Status</label>
                      <Select value={employment.status || ""} onValueChange={(v) => setEmployment({ ...employment, status: v })}>
                        <SelectTrigger data-testid="apply-employment-status" className="border-slate-200 bg-[#EEF3FC] text-slate-900"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">
                          {["Employed Full-Time", "Employed Part-Time", "Self-Employed", "Retired", "Student", "Unemployed"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {field("Employer", "employer", employment, setEmployment)}
                    {field("Job Title", "job_title", employment, setEmployment)}
                    {field("Annual Income (USD)", "annual_income", employment, setEmployment, { type: "number" })}
                    {field("Additional Income (USD)", "additional_income", employment, setEmployment, { type: "number" })}
                    {field("Source of Additional Income", "additional_income_source", employment, setEmployment)}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="mb-2 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><FileText className="h-5 w-5 text-gold" /> Documents</h2>
                  <p className="mb-6 text-sm text-slate-600">Upload required documents. Files are stored in private, encrypted storage and never shared publicly. JPG, PNG, WEBP or PDF (max 10MB).</p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger data-testid="apply-doc-type" className="border-slate-200 bg-[#EEF3FC] text-slate-900 sm:w-56"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">
                        {DOC_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gold/40 bg-gold/5 py-3 text-sm font-medium text-gold hover:bg-gold/10" data-testid="apply-upload-label">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {uploading ? "Uploading…" : "Choose file to upload"}
                      <input type="file" accept="image/*,application/pdf" onChange={uploadDoc} className="hidden" data-testid="apply-file-input" />
                    </label>
                  </div>
                  <div className="mt-5 space-y-2">
                    {app.documents?.length ? app.documents.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-[#EEF3FC] p-3">
                        <FileText className="h-5 w-5 text-gold" />
                        <div className="flex-1">
                          <div className="text-sm text-slate-900">{DOC_TYPES.find((t) => t.v === d.type)?.l || d.type}</div>
                          <div className="text-xs text-slate-500">{d.original_filename}</div>
                        </div>
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                      </div>
                    )) : <p className="text-sm text-slate-500">No documents uploaded yet.</p>}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><CheckCircle2 className="h-5 w-5 text-gold" /> Review & Submit</h2>
                  <div className="space-y-4 text-sm">
                    <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
                      <div className="mb-2 font-semibold text-gold">Applicant</div>
                      <p className="text-slate-700">{personal.first_name} {personal.last_name} · {personal.email} · {personal.phone}</p>
                      <p className="text-slate-600">{personal.address}, {personal.city}, {personal.state} {personal.zip_code}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
                      <div className="mb-2 font-semibold text-gold">Employment</div>
                      <p className="text-slate-700">{employment.status || "—"}{employment.employer ? ` · ${employment.employer}` : ""}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
                      <div className="mb-2 font-semibold text-gold">Financing Estimate</div>
                      <div className="grid grid-cols-2 gap-2 text-slate-700">
                        <span>Vehicle Price</span><span className="text-right font-mono">{fmtMoney(app.financing.vehicle_price)}</span>
                        <span>Down Payment ({app.financing.down_payment_pct}%)</span><span className="text-right font-mono">{fmtMoney(app.financing.down_payment)}</span>
                        <span>Amount Financed</span><span className="text-right font-mono">{fmtMoney(app.financing.amount_financed)}</span>
                        <span>Term</span><span className="text-right font-mono">{app.financing.term} months</span>
                        <span>Est. Monthly</span><span className="text-right font-mono text-gold">{fmtMoney(app.financing.monthly_payment)}</span>
                      </div>
                      <p className="mt-3 text-[11px] text-slate-500">This is an estimate only and does not constitute a financing offer or approval.</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-[#EEF3FC] p-4">
                      <div className="mb-2 font-semibold text-gold">Documents ({app.documents?.length || 0})</div>
                      <p className="text-slate-600">{app.documents?.map((d) => DOC_TYPES.find((t) => t.v === d.type)?.l).join(", ") || "None uploaded"}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 flex justify-between gap-3">
                <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="apply-back-btn" className="border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-900">Back</Button>
                {step < STEPS.length - 1 ? (
                  <Button onClick={nextStep} disabled={saving} data-testid="apply-next-btn" className="btn-gold rounded-full px-8">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button>
                ) : (
                  <Button onClick={submit} disabled={saving} data-testid="apply-financing-submit-btn" className="btn-gold rounded-full px-8">Submit Application</Button>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle summary */}
          <div>
            <div className="card-surface sticky top-24 overflow-hidden">
              <img src={imgUrl(app.vehicle_summary.image)} alt={vn} className="h-40 w-full object-cover" />
              <div className="p-5">
                <h3 className="font-display text-lg font-bold text-slate-900">{app.vehicle_summary.make} {app.vehicle_summary.model}</h3>
                <p className="text-sm text-slate-600">{app.vehicle_summary.year} {app.vehicle_summary.trim}</p>
                <div className="mt-4 space-y-2 border-t border-slate-200 pt-4 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Est. Monthly</span><span className="font-mono font-semibold text-gold">{fmtMoney(app.financing.monthly_payment)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Down Payment</span><span className="font-mono text-slate-900">{fmtMoney(app.financing.down_payment)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Term</span><span className="font-mono text-slate-900">{app.financing.term} mo</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
